import { bearclawPostMap } from '../data/bearclawPosts.js';
import { isPlatformPath, isAuthPath } from '../routes/platformRoutes.js';
import { blogPosts } from '../pages/blogPosts.js';
import { ORGANIZATION_ID } from '../content/siteEntityIds.js';
import { absoluteArticleAssetUrl, buildArticleSchema, buildVideoSchema } from './articleSchema.js';
import { AUTHOR_IMAGE, AUTHOR_PATH, AUTHOR_PERSON_ID } from './authorProfile.js';
import { getVideoPost, getVideoSearchMetadata } from './videoLibrary.js';
import { articleSeoDescription, articleSeoTitle } from './articleSeo.js';

/*
 * The homepage's crawler shell.
 *
 * `heading` is not a paraphrase of the <h1> — it IS the <h1>, asserted equal in
 * src/pages/Landing.test.jsx. The shell and the app render from different files
 * and a crawler reads only the shell, so without that test the two drift and the
 * page a search engine indexes stops being the page a reader sees.
 *
 * `crawlImages` exist because the shell is otherwise pure text while the page
 * itself is mostly product screenshots. Listing the real exports means the
 * crawler-visible markup carries the same evidence the reader gets.
 */
const DEFAULT_SEO = {
  title: 'BlueBear AI | Enterprise AI Agent Platform and Control Plane',
  heading: 'Deploy AI agents where your business already operates.',
  description:
    'BlueBear governs AI agents inside your business: who they represent, which tools they can use, what needs approval, and the evidence each action leaves.',
  keywords:
    'AI agents, AI workspaces, agent platform, MCP integrations, model routing, AI cost control, voice agents, white-label AI platform, Bring Your Own Cloud, BYOC',
  crawlSections: [
    ['Identity', 'Bind each agent to the tenant, workspace, person, or service it represents, then evaluate tool and credential authority outside the model prompt.'],
    ['Action', 'Agents work against connected business systems through approved MCP tools, explicit permissions, and human approval gates for sensitive actions.'],
    ['Evidence', 'Every run correlates identity, model use, tool calls, approvals, retries, cost, and outcome in one reviewable session record.'],
  ],
  crawlImages: [
    ['/product/dashboard/dashboard-desktop-light-1440.webp', 'The BlueBear dashboard, showing the platform sidebar beside a grid of active agents with their runtimes, assigned models, and key counts.'],
    ['/product/sessions/sessions-desktop-light-1440.webp', 'The BlueBear sessions view, showing total sessions, tokens, tool calls, credits, and cost over daily usage charts.'],
    ['/bluebear-logo.png', 'BlueBear'],
  ],
};

export const PUBLIC_SEO = {
  '/': DEFAULT_SEO,
  '/features': {
    title: 'Enterprise AI Agent Platform and Control Plane | BlueBear',
    heading: 'An enterprise AI agent platform for governed production work',
    description:
      'Explore BlueBear for MCP-connected agents, workflow automation, Budget Manager controls, access rights, model routing, and Bring Your Own Cloud deployment.',
    keywords:
      'BlueBear features, AI agent platform, workflow automation, voice agents, Budget Manager, BYOC, MCP tool scopes',
    crawlSections: [
      ['Governed workspaces', 'BlueBear scopes users, agents, integrations, workflows, budgets, and evidence to tenant and workspace boundaries.'],
      ['MCP-connected agents', 'Approved connections and tool actions are evaluated at an execution boundary instead of placing reusable credentials in model context.'],
      ['Routing and cost control', 'Routing policy, session usage, retries, and accepted outcomes provide the inputs for cost-aware operating decisions.'],
    ],
  },
  '/marketplace': {
    title: 'AI Agent Marketplace | BlueBear',
    heading: 'Turn anything an AI agent can use into a product',
    description:
      'Discover products from independent creators. Compare outcomes, pricing, access requirements, and delivery terms with buyer-controlled authorization.',
    keywords:
      'AI agent marketplace, MCP marketplace, sell AI skills, agent capabilities, AI agent commerce, agent software marketplace',
    schemaType: 'CollectionPage',
    crawlSections: [
      ['One Offer link', 'Creators publish products with a stable identity that humans and AI agents can inspect. Each offer declares its own delivery method, price, and terms.'],
      ['Buyer-controlled authorization', 'The buyer controls spending, permissions, connected accounts, and whether human approval is required.'],
      ['Execution and settlement', 'BlueBear records delivery or execution, issues receipts, charges the buyer, and attributes creator earnings.'],
      ['Guides for every participant', 'Creator paths explain how to package and price each product type, while the buyer guide covers trials, access, support, and receipts.'],
    ],
  },
  '/marketplace/research': {
    title: 'AI Agent Capability Marketplace Research Brief | BlueBear',
    heading: 'A commerce and authorization layer for software AI agents can use',
    description:
      'Research on BlueBear’s agent capability marketplace: incentives, stable links, hosting, scoped account access, and a falsifiable validation milestone.',
    keywords:
      'AI agent commerce, capability marketplace research, MCP marketplace, agent authorization, software entitlement, AI agent payments',
    schemaType: 'TechArticle',
    crawlSections: [
      ['Research hypothesis', 'Portable Skills distribute workflows, paid remote Capabilities provide scarce external functionality, and Solutions package accountable business outcomes.'],
      ['Participant incentives', 'Developers, Skill creators, domain specialists, Solution publishers, buyers, and BlueBear each receive economic or operational value from successful execution.'],
      ['Account access', 'Buyer-owned connections, provider-managed entitlements, brokered provisioning, and customer-cloud connectors address systems that require accounts or API credentials.'],
      ['Validation', 'The first proof requires an external creator, independent distribution, five paying buyers, repeat use, an agent-readable execution, and a creator payout.'],
    ],
  },
  '/services': {
    title: 'Enterprise AI Agent Services and Deployment | BlueBear',
    heading: 'Enterprise AI agent services built around governed production work',
    description:
      'Deploy connected agents, secure MCP access, customer-owned cloud, cost controls, healthcare workflows, or a white-label AI product with BlueBear.',
    keywords:
      'enterprise AI agent services, secure MCP integration, BYOC AI deployment, AI governance, workflow automation',
  },
  '/mcp-gateway': {
    title: 'Secure MCP Gateway for Enterprise AI Agents | BlueBear',
    heading: 'Govern how enterprise AI agents use MCP tools',
    description:
      'Control MCP tool access with tenant and workspace identity, scoped credentials, policy checks, approvals, execution receipts, and correlated audit evidence.',
    keywords:
      'secure MCP gateway, enterprise MCP gateway, Model Context Protocol security, MCP access control, MCP audit trail',
  },
  '/about': {
    title: 'About BlueBear | Enterprise AI Agent Infrastructure',
    heading: 'BlueBear builds governed infrastructure for AI agents that do real work',
    description: 'BlueBear.ai is an enterprise software company building governed infrastructure for AI agents with MCP tools, approvals, budgets, evidence, and cloud boundaries.',
    keywords: 'about BlueBear, BlueBear AI, Bluebear.ai company, Phillip Joe founder, BearClaw by BlueBear, enterprise AI agent platform, governed AI infrastructure',
    schemaType: 'AboutPage',
    /* /about is the entity anchor: the page that says what this company is and who
     * founded it. The Organization, WebSite, platform and founder nodes themselves come
     * from the site entity graph, which every document carries. */
    mainEntityId: ORGANIZATION_ID,
  },
  [AUTHOR_PATH]: {
    title: 'Phillip Joe - Founder of BlueBear.ai and AI Engineer',
    heading: 'Phillip Joe',
    description: 'Meet Phillip Joe, founder of BlueBear.ai and an AI engineer in Los Angeles working on AI agents, model orchestration, and enterprise integrations.',
    keywords: 'Phillip Joe, BlueBear.ai founder, AI engineer Los Angeles, AI agents, model routing, MCP, enterprise AI integration',
    schemaType: 'ProfilePage',
    image: AUTHOR_IMAGE,
    /* The page this Person node is ABOUT. The node itself is defined once, in the site
     * entity graph, so the 45 articles that reference it resolve wherever they are read. */
    mainEntityId: AUTHOR_PERSON_ID,
  },
  '/mcp-2026-enterprise-migration-guide': {
    title: 'MCP 2026 Enterprise Migration Guide | BlueBear',
    heading: 'MCP 2026 enterprise migration guide',
    description: 'Plan a controlled MCP 2026 migration across stateless operation, extensions, Tasks, MCP Apps, authorization, load balancing, caching, and breaking changes.',
    keywords: 'MCP 2026 migration, Model Context Protocol 2026, enterprise MCP upgrade, MCP authorization',
    schemaType: 'TechArticle',
  },
  '/ai-agent-identity-authorization': {
    title: 'AI Agent Identity and Authorization Guide | BlueBear',
    heading: 'AI agent identity and authorization for enterprise workflows',
    description: 'Design agent identity, delegation, workload authorization, scoped tool access, credential boundaries, approvals, and evidence for enterprise AI workflows.',
    keywords: 'AI agent identity, agent authorization, workload identity, AI agent access control, MCP authorization',
    schemaType: 'TechArticle',
  },
  '/agentic-ai-security-owasp-control-map': {
    title: 'OWASP Agentic AI Security Control Map | BlueBear',
    heading: 'OWASP agentic AI risks mapped to operating controls',
    description: 'Map OWASP agentic AI risks to platform controls, deployment-specific safeguards, customer responsibilities, test cases, and retained evidence.',
    keywords: 'OWASP agentic AI top 10, agentic AI security controls, AI agent risk assessment, agent security checklist',
    schemaType: 'TechArticle',
  },
  '/ai-agent-cost-per-outcome-calculator': {
    title: 'AI Agent Cost per Outcome Calculator | BlueBear',
    heading: 'AI agent cost per accepted outcome calculator',
    description: 'Calculate AI agent workflow cost across models, tools, runtime, retries, human review, exceptions, attempts, and business-accepted outcomes.',
    keywords: 'AI agent cost calculator, cost per outcome, AI FinOps calculator, agent workflow cost',
    schemaType: 'WebApplication',
  },
  '/services/agents': {
    title: 'Connected AI Agents and Workflow Automation | BlueBear',
    description:
      'Connect governed AI agents to approved business tools, MCP connections, voice channels, workflow templates, approvals, and retained run evidence.',
    keywords: 'connected AI agents, MCP tool integration, voice agents, governed workflow automation, human handoffs',
  },
  '/services/white-label': {
    title: 'White-Label AI Platform and Tenant Workspaces | BlueBear',
    description:
      'Launch a branded AI product with tenant workspaces, MCP tool scopes, organization rights, workspace permissions, budgets, and customer-facing agent experiences.',
    keywords: 'white-label AI platform, tenant workspaces, MCP scopes, branded AI agents, customer AI portal',
  },
  '/services/health': {
    title: 'Healthcare AI Workflows and Audit Trails | BlueBear',
    description:
      'Coordinate healthcare operations with auditable AI workflows for DME intake, OCR review, signatures, prior authorization, and exception handling.',
    keywords: 'healthcare AI workflows, DME automation, prior authorization, NikoHealth, healthcare operations',
  },
  '/services/optimization': {
    title: 'AI Model Routing and Budget Manager | BlueBear',
    description:
      'Control AI spend with model-routing policy, scoped Budget Manager limits, workflow attribution, and cost per accepted outcome.',
    keywords: 'AI model routing, Budget Manager, AI cost optimization, cost per accepted outcome, AI FinOps',
  },
  '/services/deploy': {
    title: 'BYOC and Private AI Agent Deployment | BlueBear',
    description:
      'Deploy governed BlueBear agents in managed cloud, customer-owned accounts, private clusters, or on-premises with explicit boundaries and responsibilities.',
    keywords: 'Bring Your Own Cloud, BYOC AI agents, on-premise AI deployment, customer-owned cloud, private Kubernetes',
  },
  '/wordpress': {
    title: 'BlueBear for WordPress - Governed AI Site Operator Plugin',
    heading: 'Operate WordPress with a governed AI workspace',
    description:
      'Connect each WordPress site to an isolated BlueBear workspace with tenant-linked users, scoped credentials, and approval-gated changes.',
    keywords:
      'BlueBear WordPress plugin, WordPress AI site operator, WordPress MCP, AI WordPress management, governed WordPress automation',
    schemaType: 'SoftwareApplication',
    crawlSections: [
      ['One workspace per site', 'Each WordPress installation receives a site-specific workspace, operator, MCP binding, and credential boundary.'],
      ['Aligned tenant identities', 'New WordPress users, profile updates, and role changes map through stable site-scoped external identities.'],
      ['Approval-gated operations', 'The operator can inspect and prepare work while consequential WordPress changes remain subject to explicit approval.'],
    ],
  },
  '/pricing-info': {
    title: 'BlueBear Pricing for AI Agents and Workspaces',
    description:
      'Understand BlueBear pricing for AI agents, workspaces, model usage, workflows, voice agents, deployment activity, and Budget Manager governance.',
    keywords: 'BlueBear pricing, AI agent pricing, Budget Manager, AI workspace usage, model usage costs',
  },
  '/security': {
    title: 'Enterprise AI Agent Security and Audit Trails | BlueBear',
    heading: 'Security boundaries for enterprise AI agents and MCP-connected work',
    description:
      'Review BlueBear tenant isolation, agent identity, workspace permissions, MCP tool scopes, credential boundaries, approvals, and audit trails.',
    keywords: 'AI platform security, MCP scopes, workspace permissions, tenant isolation, AI audit trails',
    crawlSections: [
      ['Identity and workspace boundaries', 'BlueBear carries tenant, organization, workspace, user or service identity, and agent context into governed requests.'],
      ['MCP tool and credential boundaries', 'Assigned connections, permitted actions, policy results, approvals, and credential references keep tool authority outside prompt text.'],
      ['Evidence for review', 'Correlated sessions, model routes, tool calls, approvals, failures, and outcomes support incident investigation and control review.'],
      ['Deployment-specific confirmation', 'Customers confirm infrastructure, retention, encryption, authentication, and compliance requirements for the selected deployment during architecture and security review.'],
    ],
  },
  '/case-studies': {
    title: 'AI Agent Case Studies and Workflows | BlueBear',
    heading: 'Representative workflows and how a pilot measures them',
    description:
      'Explore representative AI agent workflows for healthcare, finance, connected tools, and white-label platforms, and how a pilot would measure each one.',
    keywords: 'AI agent case studies, workflow automation case studies, white-label AI, BlueBear customers',
  },
  '/governed-agent-execution-proof': {
    title: 'AI Agent Execution Evidence | BlueBear',
    heading: 'Audit a governed AI agent run end to end',
    description:
      'Inspect the identity, authority, approval, tool, cost, system result, and accepted-outcome evidence required to audit an enterprise AI agent run.',
    keywords:
      'AI agent audit evidence, governed agent execution, AI agent audit trail, ERP agent approval, agent outcome verification',
    schemaType: 'TechArticle',
    crawlSections: [
      ['Identity and authority', 'Correlate tenant, user or service, agent, workspace, tool, action, credential reference, and permission decision before execution.'],
      ['Execution evidence', 'Review model routing, MCP activity, tool calls, tokens, timestamps, status, retries, and cost for the same governed run.'],
      ['Business verification', 'Link the downstream system-of-record identifier and business acceptance decision instead of treating a model response as a completed outcome.'],
      ['Completeness protocol', 'Score only required fields backed by captured or linked artifacts, and leave missing evidence visibly incomplete.'],
    ],
  },
  '/bearclaw': {
    title: 'BearClaw Personal AI Workspace | BlueBear',
    description:
      'BearClaw gives individuals a persistent AI workspace for files, tasks, connected apps, ongoing work, and reusable context.',
    keywords: 'BearClaw, personal AI workspace, AI files, connected apps, persistent AI workspace',
  },
  '/bearclaw/news': {
    title: 'BearClaw Personal AI Workspace News | BlueBear',
    description:
      'Read BearClaw guides, product updates, workflow ideas, security notes, and connected-app articles for personal AI workspaces.',
    keywords: 'BearClaw news, personal AI workspace guides, AI workflow guides, connected app guides',
  },
  '/compute': {
    title: 'Secure AI Compute Links and Runtime Access | BlueBear',
    description:
      'Share secure compute links for AI workloads with scoped permissions, budget caps, expiration, retained logs, and runtime proof.',
    keywords: 'compute links, secure AI runtime, AI workload access, budget caps, runtime logs',
  },
  '/contact': {
    title: 'Contact BlueBear for Enterprise AI Agents',
    description:
      'Contact BlueBear to plan connected AI agents, Bring Your Own Cloud deployment, Budget Manager controls, security policies, and workflow automation.',
    keywords: 'contact BlueBear, AI agent consultation, BYOC deployment, workflow automation planning',
  },
  '/join-waitlist': {
    title: 'Join the BlueBear AI Agent Platform',
    description:
      'Join BlueBear to get access to governed AI workspaces, connected agents, workflow automation, Budget Manager controls, and BYOC deployment options.',
    keywords: 'join BlueBear, AI workspace waitlist, governed AI agents, BYOC AI platform',
  },
  '/privacy': {
    title: 'BlueBear Privacy Policy',
    description: 'Read the BlueBear privacy policy for data handling, privacy practices, and user rights.',
    keywords: 'BlueBear privacy policy, AI platform privacy',
  },
  '/terms': {
    title: 'BlueBear Terms of Service',
    description: 'Read the BlueBear terms of service for platform access, account rules, and service terms.',
    keywords: 'BlueBear terms, AI platform terms of service',
  },
  '/insights': {
    title: 'AI Agent Insights: MCP, Security and Control | BlueBear',
    description:
      'Read BlueBear insights on AI agent deployment, MCP integrations, cost optimization, Kubernetes patterns, and white-label platform launches.',
    keywords: 'AI agent insights, MCP guide, AI cost optimization, Kubernetes AI agents, white-label AI guide',
  },
  '/videos': {
    title: 'BlueBear Videos - Enterprise AI Agent Field Guides',
    heading: 'Watch BlueBear enterprise AI agent field guides',
    description: 'Watch concise BlueBear videos about MCP security, agent control planes, BYOC, AI FinOps, model routing, and governed enterprise AI operations.',
    keywords: 'BlueBear videos, AI agent videos, MCP video guide, AI FinOps video, BYOC AI agents',
    crawlSections: [
      ['Dedicated watch pages', 'Each approved video has one indexable page where watching the video is the primary purpose.'],
      ['Accessible supporting material', 'Captions, visible transcripts, stable posters, and related implementation guidance accompany each video.'],
      ['Evidence-led topics', 'Videos connect operating concepts to BlueBear product boundaries without inventing customer measurements.'],
    ],
  },
};

const blogPostMap = blogPosts.reduce((acc, post) => {
  acc[post.slug] = post;
  return acc;
}, {});

function normalizePath(pathname = '/') {
  const clean = pathname.split('?')[0].split('#')[0] || '/';
  if (clean.length > 1 && clean.endsWith('/')) return clean.slice(0, -1);
  return clean;
}

export function getPublicSeoForPath(pathname = '/') {
  const path = normalizePath(pathname);
  if (PUBLIC_SEO[path]) return { ...PUBLIC_SEO[path], path };

  if (path.startsWith('/videos/')) {
    const videoPost = getVideoPost(path.replace('/videos/', ''));
    if (videoPost) {
      const videoSearchMetadata = getVideoSearchMetadata(videoPost);
      return {
        title: videoSearchMetadata.title,
        headline: `${videoPost.title} visual field guide`,
        description: videoSearchMetadata.description,
        keywords: `${videoPost.title}, BlueBear video, AI agent field guide`,
        image: videoPost.videoPoster,
        videoUrl: videoPost.videoUrl,
        videoPoster: videoPost.videoPoster,
        videoDuration: videoPost.videoDuration,
        videoTranscript: videoPost.videoTranscript,
        datePublished: videoPost.date,
        type: 'video',
        path,
      };
    }
  }

  if (path.startsWith('/bearclaw/news/')) {
    const slug = path.replace('/bearclaw/news/', '');
    const post = bearclawPostMap[slug];
    if (post) {
      return {
        title: articleSeoTitle(post, 'BearClaw'),
        description: articleSeoDescription(post),
        keywords: `BearClaw, personal AI workspace, ${post.categoryLabel}, connected AI workspace`,
        type: 'article',
        path,
      };
    }
  }

  /*
   * /use/<slug> — one marketplace offer.
   *
   * The copy for this route is not in this repository: it is read from the
   * gateway when the page mounts, and utils/offerSeo.js writes the offer's own
   * title, description and Product node over what is returned here. This is
   * what a crawler and the browser tab get before that resolves, and it is what
   * makes the canonical for the route correct.
   *
   * There is deliberately NO PUBLIC_SEO key for /use: a literal key is
   * prerendered by scripts/generate-article-pages.mjs and written into the
   * sitemap, and a data-driven route shipped as a static page would say nothing
   * and rank as one.
   */
  if (path.startsWith('/use/') && path.split('/').length === 3) {
    return {
      title: 'BlueBear Marketplace Offer',
      description:
        'A capability published on the BlueBear marketplace: what it does, what it costs, what it needs from you, and how to request access.',
      keywords: 'BlueBear marketplace, AI capability marketplace, agent solution, MCP capability, published capability',
      path,
    };
  }

  const insightSlug = path.slice(1);
  const insight = blogPostMap[insightSlug];
  if (insight) {
    return {
      title: articleSeoTitle(insight),
      description: articleSeoDescription(insight),
      keywords: `BlueBear insights, ${insight.title}, AI agents, MCP, AI infrastructure`,
      image: insight.heroImage,
      datePublished: insight.date,
      articleSection: insight.categoryLabel,
      citations: insight.sourceUrls,
      wordCount: insight.contentHtml.replace(/<[^>]+>/g, ' ').trim().split(/\s+/).filter(Boolean).length,
      videoUrl: insight.videoUrl,
      videoPoster: insight.videoPoster,
      videoCaption: insight.videoCaption,
      videoDuration: insight.videoDuration,
      videoTranscript: insight.videoTranscript,
      headline: insight.title,
      type: 'article',
      path,
    };
  }

  return null;
}

/*
 * The structured data for a non-article public route.
 *
 * One builder, two callers: `applyStructuredData` below (client-side, after React
 * mounts) and `renderPublicRoute` in scripts/generate-article-pages.mjs (build-time,
 * for crawlers that never run JavaScript). They used to be two hand-kept copies of
 * the same object literal, which is exactly how they drift.
 *
 * This node only ever REFERENCES the site's entities. The nodes it points at —
 * #organization, #website, #platform and the founder's Person — are defined once by
 * src/utils/siteEntityGraph.js, which MarketingHeader emits client-side and
 * `siteEntityGraphScript()` writes into every prerendered document. Both render paths
 * therefore resolve the same `@id`s, and neither restates them.
 */
export function buildPublicPageSchema(routePath, seo) {
  const url = `https://bluebear.ai${routePath === '/' ? '/' : routePath}`;
  return {
    '@context': 'https://schema.org',
    '@type': seo.schemaType || 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: seo.heading || seo.title,
    description: seo.description,
    isPartOf: { '@id': 'https://bluebear.ai/#website' },
    about: { '@id': 'https://bluebear.ai/#platform' },
    publisher: { '@id': 'https://bluebear.ai/#organization' },
    inLanguage: 'en-US',
    ...(seo.mainEntityId ? { mainEntity: { '@id': seo.mainEntityId } } : {}),
    ...(seo.schemaType === 'WebApplication' ? {
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      browserRequirements: 'Requires JavaScript for interactive calculation',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    } : {}),
  };
}

/* Exported for utils/offerSeo.js, which writes the head for /use/<slug>
 * after its offer loads. One implementation of "upsert a meta tag", so the
 * two paths cannot drift on a detail like creating a duplicate node. */
export function setMeta(doc, selector, attrs, value) {
  let element = doc.head.querySelector(selector);
  if (!element) {
    element = doc.createElement('meta');
    Object.entries(attrs).forEach(([key, attrValue]) => element.setAttribute(key, attrValue));
    doc.head.appendChild(element);
  }
  element.setAttribute('content', value);
}

export function setCanonical(doc, url) {
  let link = doc.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = doc.createElement('link');
    link.setAttribute('rel', 'canonical');
    doc.head.appendChild(link);
  }
  link.setAttribute('href', url);
}

function applyStructuredData(doc, seo, url, image) {
  let script = doc.head.querySelector('script[data-bluebear-public-schema]');
  if (seo.type !== 'article' && seo.type !== 'video' && !seo.schemaType) {
    script?.remove();
    return;
  }
  if (!script) {
    script = doc.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-bluebear-public-schema', 'true');
    doc.head.appendChild(script);
  }
  if (seo.type !== 'article') {
    script.textContent = JSON.stringify(buildPublicPageSchema(seo.path, seo));
    return;
  }
  if (seo.type === 'video') {
    script.textContent = JSON.stringify(buildVideoSchema({
      title: seo.headline || seo.title,
      summary: seo.description,
      watchUrl: url,
      videoUrl: seo.videoUrl,
      thumbnailUrl: seo.videoPoster || image,
      uploadDate: seo.datePublished,
      duration: seo.videoDuration,
      transcript: seo.videoTranscript,
    }));
    return;
  }
  script.textContent = JSON.stringify(buildArticleSchema({
    title: seo.headline || seo.title,
    summary: seo.description,
    url,
    image,
    datePublished: seo.datePublished,
    dateModified: seo.dateModified,
    articleSection: seo.articleSection,
    citations: seo.citations,
    wordCount: seo.wordCount,
    keywords: seo.keywords,
    videoUrl: seo.videoUrl,
    videoPoster: seo.videoPoster,
    videoCaption: seo.videoCaption,
    videoDuration: seo.videoDuration,
    videoTranscript: seo.videoTranscript,
  }));
}

export function applyPublicSeoToDocument(pathname, doc = document) {
  if (/^\/marketplace\/guest\//.test(pathname) || isPlatformPath(pathname) || isAuthPath(pathname)) {
    if (doc?.head) setMeta(doc, 'meta[name="robots"]', { name: 'robots' }, 'noindex, nofollow, noarchive');
    return false;
  }
  const seo = getPublicSeoForPath(pathname);
  if (!seo || !doc?.head) return false;
  setMeta(doc, 'meta[name="robots"]', { name: 'robots' }, 'index, follow');
  const image = absoluteArticleAssetUrl(seo.image, '/og-image.png');

  const url = `https://bluebear.ai${seo.path === '/' ? '/' : seo.path}`;
  doc.title = seo.title;
  setMeta(doc, 'meta[name="description"]', { name: 'description' }, seo.description);
  setMeta(doc, 'meta[name="keywords"]', { name: 'keywords' }, seo.keywords || DEFAULT_SEO.keywords);
  setMeta(doc, 'meta[property="og:title"]', { property: 'og:title' }, seo.title);
  setMeta(doc, 'meta[property="og:description"]', { property: 'og:description' }, seo.description);
  setMeta(doc, 'meta[property="og:image"]', { property: 'og:image' }, image);
  setMeta(doc, 'meta[property="og:url"]', { property: 'og:url' }, url);
  setMeta(doc, 'meta[property="og:type"]', { property: 'og:type' }, seo.type || 'website');
  if (seo.videoUrl) {
    setMeta(doc, 'meta[property="og:video"]', { property: 'og:video' }, absoluteArticleAssetUrl(seo.videoUrl));
  }
  if (seo.type === 'article') {
    setMeta(doc, 'meta[name="robots"]', { name: 'robots' }, 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    setMeta(doc, 'meta[property="article:published_time"]', { property: 'article:published_time' }, seo.datePublished || '');
    setMeta(doc, 'meta[property="article:modified_time"]', { property: 'article:modified_time' }, seo.dateModified || seo.datePublished || '');
    setMeta(doc, 'meta[property="article:section"]', { property: 'article:section' }, seo.articleSection || 'BlueBear Insights');
  }
  setMeta(doc, 'meta[name="twitter:image"]', { name: 'twitter:image' }, image);
  applyStructuredData(doc, seo, url, image);
  setMeta(doc, 'meta[name="twitter:title"]', { name: 'twitter:title' }, seo.title);
  setMeta(doc, 'meta[name="twitter:description"]', { name: 'twitter:description' }, seo.description);
  setCanonical(doc, url);
  return true;
}
